import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import {
  conditionalRequirementMessages,
  isNestedError,
  nestedBlankRequiredErrors,
  nestedFieldErrors,
} from 'shared/components/forms/SchemaFormTemplates/validateActionConfigs';
import { boundingBoxErrors } from 'shared/components/forms/SchemaFormTemplates/boundingBox';
import { polygonErrors } from 'shared/components/forms/SchemaFormTemplates/polygon';

// Validates one action's form data against its JSON schema before it is
// PATCHed, mirroring the tolerance of the legacy save path in
// ManageRouteDetails (custom Gundi keywords, if/then/else composition).
// Returns { fieldName: message } — empty when the data is valid — in the shape
// SchemaForm's `errors` prop expects.
export function validateActionData(
  schema: any,
  data: any,
  t: (key: any, options?: any) => string = (key: any) => key,
  // The action's ui_schema: fields it marks with a bbox widget are checked for a
  // valid box, which the JSON schema (a plain string) cannot express.
  uiSchema?: any,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!schema?.properties || typeof schema.properties !== 'object')
    return errors;

  const ajv = new Ajv({
    allErrors: true,
    verbose: true,
    strictTypes: false,
    strict: false,
  });
  addFormats(ajv);
  addKeywords(ajv);

  // Omitted keys stay omitted: filling them with null makes AJV judge an absent
  // optional field against its `type` and reject perfectly valid data. Genuine
  // `required` violations still arrive with params.missingProperty, which the
  // error mapping below turns into a field-level message.
  const normalized: Record<string, any> = { ...(data || {}) };

  // Same if/then/else flattening as the legacy validator: pick the branch the
  // data matches and validate against base + branch properties/required.
  let effectiveSchema = schema;
  if (schema.if && (schema.then || schema.else)) {
    try {
      const branch = ajv.compile(schema.if)(normalized)
        ? schema.then
        : schema.else;
      if (branch && typeof branch === 'object') {
        effectiveSchema = {
          ...schema,
          properties: { ...schema.properties, ...branch.properties },
          // Merge, don't replace: a branch's `required` adds to the base
          // one, so dropping the base list would skip client validation
          // of base fields and leave the user with a generic 400.
          required: Array.from(
            new Set([...(schema.required || []), ...(branch.required || [])]),
          ),
        };
      }
    } catch (e) {
      console.error('failed to evaluate schema if/then/else', e);
    }
  }

  let validate;
  try {
    validate = ajv.compile(effectiveSchema);
  } catch (e) {
    // An uncompilable schema must not block saving — the backend validates too.
    console.error('failed to compile action schema', e);
    return errors;
  }

  Object.assign(
    errors,
    nestedBlankRequiredErrors(effectiveSchema, normalized, t),
  );
  if (!validate(normalized)) {
    Object.entries(
      nestedFieldErrors(validate.errors, normalized, t, effectiveSchema),
    ).forEach(([path, message]) => {
      if (!errors[path]) errors[path] = message;
    });
    (validate.errors || []).forEach((err: any) => {
      if (isNestedError(err)) return;
      const field =
        err.instancePath?.split('/')[1] || err.params?.missingProperty;
      if (field && !errors[field])
        errors[field] = `Error in field '${field}': ${err.message}`;
    });
  }
  // Same grouping as the creation screens: a conditional's alternatives are
  // flagged together, so an edit and a create explain the rule the same way.
  Object.assign(
    errors,
    conditionalRequirementMessages(ajv, schema, normalized, t),
  );
  Object.assign(errors, boundingBoxErrors(uiSchema, normalized, t));
  Object.assign(errors, polygonErrors(uiSchema, normalized, t));
  return errors;
}
